import type { PlainMessage } from '@bufbuild/protobuf';

import api from '@/shared/api/api';
import type {
  NoteList,
  NoteListItem,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import { callScribe } from './scribeError';
import { fetchAllCatalogPages } from './scribePaging';

// An item id is the one scribe minted, or empty or `tmp_…` for a new item.
export type NoteListInput = {
  name: string;
  description?: string;
  items: PlainMessage<NoteListItem>[];
};

// Shared value lists of an organization (anaesthetics, materials, labs). The
// messages are scribe's own: a list block reads them as they are.
export const noteListsApi = {
  list: async (params: {
    organizationId: string;
    // The lists a template references, deleted ones included.
    ids?: string[];
    language?: string;
  }): Promise<NoteList[]> => {
    const page = await fetchAllCatalogPages(async (Offset, Limit) => {
      const response = await callScribe(
        api.noteList.list({
          OrganizationID: params.organizationId,
          IDs: params.ids,
          Language: params.language,
          Offset,
          Limit,
        }),
      );
      return { items: response.Lists, total: response.Total };
    });
    return page.items;
  },

  create: async (
    params: NoteListInput & {
      organizationId: string;
      code: string;
      language: string;
    },
  ): Promise<NoteList | undefined> => {
    const response = await callScribe(
      api.noteList.create({
        OrganizationID: params.organizationId,
        Code: params.code,
        Language: params.language,
        Name: params.name,
        Description: params.description,
        Items: params.items,
      }),
    );
    return response.List;
  },

  // A stale revision is refused; the caller reads the list again.
  update: async (
    list: Pick<NoteList, 'ID' | 'Revision'>,
    input: NoteListInput,
  ): Promise<NoteList | undefined> => {
    const response = await callScribe(
      api.noteList.update({
        ListID: list.ID,
        BaseRevision: list.Revision,
        Name: input.name,
        Description: input.description,
        Items: input.items,
      }),
    );
    return response.List;
  },

  remove: async (listId: string): Promise<void> => {
    await callScribe(api.noteList.delete({ ListID: listId }));
  },
};
